import { useQuery } from '@tanstack/react-query';

import type {
  MonthlySettlementResponse,
  MonthlySettlementSummary,
  SettlementRequest,
} from '@marxbiotech/shared-types/bff';

import { api } from '@lib/api/client';
import { paymentKeys } from '@lib/query/options/payment';

/**
 * Monthly settlement info interface for frontend consumption.
 * Uses camelCase to match frontend conventions.
 */
export interface MonthlySettlementInfo {
  // 帳單期間資訊（永遠基於當前日期的上個月）
  billingPeriod: {
    year: number;
    month: number;
    endDate: string; // 上個月最後一天 (yyyy-MM-dd 格式)
  };

  // 上月月底的快照
  lastMonthSnapshot: {
    loanOutstanding: number;
    cashBalance: number;
    amountDue: number; // loanOutstanding - cashBalance
    rewardBalance: number;
    refundBalance: number;
    snapshotDate: string;
  };

  // 本月到目前的還款情況
  currentMonthProgress: {
    totalRepayment: number; // 本月總還款金額 (LOAN_REPAYMENT)
    repaymentCount: number; // 還款次數
    totalNewLoan: number; // 本月新增借款金額 (LOAN_PAYOUT)
    newLoanCount: number; // 新增借款次數
  };

  // 當前狀態
  current: {
    loanOutstanding: number;
    cashBalance: number;
    rewardBalance: number;
    refundBalance: number;
    amountDue: number;
    remainingFromLastMonth: number; // 上月待結清中尚未還清的部分
  };

  // 待確認入金資訊
  pendingRequests: {
    total: number; // 待確認總金額
    count: number; // 待確認筆數
    hasCurrentMonthPeriod: boolean; // 是否有當前月 PERIOD 請求
    requests: SettlementRequest[]; // 原始資料
  };
}

/**
 * Transform BFF response (snake_case) to frontend format (camelCase)
 * @internal Exported for testing
 */
export function transformToFrontendFormat(
  data: MonthlySettlementSummary
): MonthlySettlementInfo {
  return {
    billingPeriod: {
      year: data.billing_period.year,
      month: data.billing_period.month,
      endDate: data.billing_period.end_date,
    },
    lastMonthSnapshot: {
      loanOutstanding: data.last_month_snapshot.loan_outstanding,
      cashBalance: data.last_month_snapshot.cash_balance,
      amountDue: data.last_month_snapshot.amount_due,
      rewardBalance: data.last_month_snapshot.reward_balance,
      refundBalance: data.last_month_snapshot.refund_balance,
      snapshotDate: data.last_month_snapshot.snapshot_date,
    },
    currentMonthProgress: {
      totalRepayment: data.current_month_progress.total_repayment,
      repaymentCount: data.current_month_progress.repayment_count,
      totalNewLoan: data.current_month_progress.total_new_loan,
      newLoanCount: data.current_month_progress.new_loan_count,
    },
    current: {
      loanOutstanding: data.current.loan_outstanding,
      cashBalance: data.current.cash_balance,
      rewardBalance: data.current.reward_balance,
      refundBalance: data.current.refund_balance,
      amountDue: data.current.amount_due,
      remainingFromLastMonth: data.current.remaining_from_last_month,
    },
    pendingRequests: {
      total: data.pending_requests.total,
      count: data.pending_requests.count,
      hasCurrentMonthPeriod: data.pending_requests.has_current_month_period,
      requests: data.pending_requests.requests,
    },
  };
}

/**
 * Hook to fetch monthly settlement information.
 *
 * Fetches aggregated settlement data including billing period, last month snapshot,
 * current month progress, current balances, and pending settlement requests.
 *
 * @param year - Optional billing year (defaults to last month's year)
 * @param month - Optional billing month (defaults to last month)
 */
export const useMonthlySettlementInfo = (
  year?: number,
  month?: number
) => {
  const {
    data: rawData,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: [...paymentKeys.all, 'monthlySettlement', year, month],
    queryFn: async (): Promise<MonthlySettlementSummary> => {
      const queryParams = new URLSearchParams();
      if (year !== undefined) queryParams.set('year', String(year));
      if (month !== undefined) queryParams.set('month', String(month));

      const queryString = queryParams.toString();
      const url = `/payment/accounts/current/monthly-settlement${queryString ? `?${queryString}` : ''}`;

      const response = await api.get<MonthlySettlementResponse>(url);
      return response.data.monthly_settlement;
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    data: rawData ? transformToFrontendFormat(rawData) : null,
    isLoading: isPending,
    isError,
    error,
  };
};
